/**
 * What a Word List term has to sit next to before it counts as a match.
 *
 * This is the fifth slot of `regex_any`/`regex_none` (`separator` in both
 * engines). It existed in the engines all along, but the canvas never read or
 * wrote it, so it was invisible -- and any value set by hand was dropped on the
 * next save. Absent means the engines' default (`\b`), which is what every node
 * built before this setting existed uses, so leaving it absent changes nothing.
 *
 * Only two choices are offered. The engines also accept `\s`, but they build it
 * as `\sTERM\s`, which needs a real space on both sides and so never matches a
 * term at the very start or end of a post. Offering it would add a new
 * boundary nobody can see, which is the problem this setting exists to fix.
 */
type TermBoundary = "word" | "anywhere" | "custom";

/**
 * The stored value for each choice the editor offers.
 *
 * "Whole words" is written as an explicit `\b` rather than by clearing the
 * slot. Both engines treat `\b` exactly as they treat an absent slot (it is
 * their default, and 106 live conditions already carry it), but clearing a
 * prop to `undefined` does not reach the save: the canvas showed "Whole words"
 * while the saved manifest kept "anywhere". A node that never touches the
 * setting still saves no fifth slot at all.
 */
export const BOUNDARY_SEPARATOR = {
  word: "\\b",
  anywhere: "",
} as const;

export function termBoundary(separator: string | undefined): TermBoundary {
  if (separator === undefined || separator === null || separator === "\\b") {
    return "word";
  }
  if (separator === "") {
    return "anywhere";
  }
  return "custom";
}

export const BOUNDARY_LABEL: Record<TermBoundary, string> = {
  word: "Whole words",
  anywhere: "Anywhere in the text",
  custom: "Custom boundary",
};

export const BOUNDARY_HELP: Record<TermBoundary, string> = {
  word: "A term can't touch a letter, number, or hyphen on either side, so “art” misses “cartoon” and “pixel-art”. Japanese, Chinese, and Korean characters don't need a space.",
  anywhere:
    "A term matches inside longer words too, so “art” also matches “cartoon”.",
  custom: "This node uses a boundary set outside the editor. It is kept as is.",
};
